import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, Building2, Users, FileText, ShieldCheck, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 2.6.4 — Pagina "Convenzioni Assegnate": mostra tutte le convenzioni di cui
 * l'utente è referente (top_agent) o admin. Da qui l'agente entra nel workspace
 * isolato di una convenzione (Rubrica + Offerte filtrate per convenzione_id).
 */
export default function ConvenzioniAssegnatePage() {
  const [loading, setLoading] = useState(true);
  const [convenzioni, setConvenzioni] = useState([]);
  const { getAuthHeader, enterConvenzione, user } = useAuth();
  const navigate = useNavigate();

  const fetchConvenzioni = async () => {
    try {
      setLoading(true);
      // 2.6.5 — Admin/Top Agent/Agente vedono solo le proprie. Super Admin vede tutte
      // le convenzioni (per gestione trasversale).
      const onlyMine = user?.role !== 'super_admin';
      const res = await axios.get(`${API}/agent/convenzioni-assegnate${onlyMine ? '?only_mine=true' : ''}`, {
        headers: getAuthHeader(),
      });
      setConvenzioni(res.data || []);
    } catch (e) {
      console.error('Error fetch convenzioni:', e);
      toast.error('Errore nel caricamento delle convenzioni assegnate');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConvenzioni();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnter = async (conv) => {
    try {
      // Verifica access con backend
      const res = await axios.get(`${API}/agent/convenzione/${conv.id}/access-check`, {
        headers: getAuthHeader(),
      });
      if (res.data?.allowed) {
        enterConvenzione({
          id: res.data.convenzione.id,
          nome: res.data.convenzione.nome,
          codice_univoco: res.data.convenzione.codice_univoco,
          logo_url: res.data.convenzione.logo_url,
        });
        toast.success(`Sei entrato nel workspace "${conv.nome}"`);
        navigate('/dashboard/rubrica');
      } else {
        toast.error('Accesso negato al workspace');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile entrare nel workspace');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const activeConvs = convenzioni.filter(c => !c.is_default);

  return (
    <div className="space-y-6" data-testid="convenzioni-assegnate-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
          <Building2 className="w-8 h-8 text-emerald-600" />
          Convenzioni Assegnate
        </h1>
        <p className="text-slate-600 mt-2">
          Gestisci in modo isolato clienti e pratiche generate dai canali convenzionati di cui sei referente.
        </p>
      </div>

      {activeConvs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium">Nessuna convenzione assegnata</p>
            <p className="text-sm mt-1">Contatta l&apos;amministratore per farti assegnare come referente.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {activeConvs.map(conv => (
            <Card
              key={conv.id}
              className="hover:shadow-lg transition-shadow border-slate-200"
              data-testid={`convenzione-card-${conv.codice_univoco}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg font-bold text-slate-900 truncate">
                      {conv.nome}
                    </CardTitle>
                    <Badge variant="outline" className="mt-1 text-[10px] uppercase tracking-wide font-mono">
                      {conv.codice_univoco}
                    </Badge>
                  </div>
                  {conv.logo_url && (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${conv.logo_url}`}
                      alt={conv.nome}
                      className="h-10 w-10 object-contain rounded"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                </div>
                {conv.descrizione && (
                  <p className="text-xs text-slate-500 mt-2 line-clamp-2">{conv.descrizione}</p>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                  <div className="bg-slate-50 rounded p-2">
                    <div className="flex items-center justify-center text-slate-600 mb-1">
                      <Users className="w-3 h-3" />
                    </div>
                    <div className="text-xl font-bold text-slate-900">{conv.stats.clienti}</div>
                    <div className="text-[10px] text-slate-500 uppercase">Clienti</div>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <div className="flex items-center justify-center text-slate-600 mb-1">
                      <FileText className="w-3 h-3" />
                    </div>
                    <div className="text-xl font-bold text-slate-900">{conv.stats.offerte_attive}</div>
                    <div className="text-[10px] text-slate-500 uppercase">Offerte</div>
                  </div>
                  <div className="bg-emerald-50 rounded p-2">
                    <div className="flex items-center justify-center text-emerald-700 mb-1">
                      <ShieldCheck className="w-3 h-3" />
                    </div>
                    <div className="text-xl font-bold text-emerald-800">{conv.stats.visto_verde_attivi}</div>
                    <div className="text-[10px] text-emerald-700 uppercase">V. Verde</div>
                  </div>
                </div>
                <Button
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => handleEnter(conv)}
                  data-testid={`btn-enter-conv-${conv.codice_univoco}`}
                >
                  Entra in gestione
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
